import { type FormEvent, type JSX, useEffect, useMemo, useRef, useState } from 'react';

import { usePageMeta } from '../../discovery/page-meta';
import { AppLink } from '../../discovery/router';
import { SessionApiError } from '../../session/client';
import { useSession } from '../../session/SessionProvider';
import { SiteShell } from '../SitePages';
import { type Place } from '../store/store-api';
import { useLoad, usePlace } from '../store/store-hooks';
import { Icon, PATHS } from '../store/store-icons';
import { LocationPicker } from '../store/StorePage';
import {
  type DirectoryPage,
  type DirectoryPlace,
  type DirectoryRequest,
  type Mode,
  type Sort,
  type TaxonomyCategory,
  directoryQuery,
  getCategories,
  getDirectory,
  getSavedAddresses,
  registerInterest,
} from './places-api';
import {
  DEPARTMENTS,
  type DepartmentId,
  MODES,
  NEEDS,
  departmentCategories,
  directionsUrl,
  distanceLabel,
  fulfilChip,
  needCategories,
  projectPins,
  ratingLabel,
  rowOf,
  seeAllLabel,
  statusChip,
  tileOf,
  whereLabel,
} from './places-model';
import './places.css';

/**
 * "Places near you" (`/nearby`) — website stage 7, built from
 * `Just Treated Places Near You.dc.html`.
 *
 * Public: logged out and logged in alike. A signed-in person with no postcode
 * chosen on this browser starts from their default saved address.
 *
 * Every value on the page is the platform's: the places are the storefront
 * directory (`GET consumer/storefronts`), which lists exactly the shops whose
 * offers a search from the postcode reaches; hours, fees, minimums, ratings and
 * promotions are each shop's own record, shown only where recorded; distance
 * is computed from the postcode. What the frame draws with nothing behind it
 * (owner decision D5) is not drawn: Sponsored, Local heroes, the Fastest sort,
 * "Available today", delivery minutes, review text. The map is the page's own
 * drawing, to scale — no third-party tiles (D7) — with a "Get directions"
 * hand-off. "Use your location" is Coming soon: the site's policy blocks
 * geolocation. The app banner is omitted: the apps are not published.
 */

const SORTS: readonly { readonly id: Sort; readonly label: string }[] = [
  { id: 'recommended', label: 'Recommended' },
  { id: 'nearest', label: 'Nearest' },
  { id: 'rating', label: 'Highest rated' },
];
const DISTANCES = [1, 3, 5, 10] as const;
const PAGE = 20;

interface Filters {
  readonly open: boolean;
  readonly four: boolean;
  readonly offers: boolean;
  readonly freeDelivery: boolean;
  readonly nhs: boolean;
}
const NO_FILTERS: Filters = { open: false, four: false, offers: false, freeDelivery: false, nhs: false };

function useWide(query: string): boolean {
  const read = (): boolean => typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  const [matches, setMatches] = useState(read);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const list = window.matchMedia(query);
    const follow = (): void => setMatches(list.matches);
    list.addEventListener?.('change', follow);
    return () => list.removeEventListener?.('change', follow);
  }, [query]);
  return matches;
}

export function PlacesPage(): JSX.Element {
  const session = useSession();
  const { place, recent, choose } = usePlace();
  const [mode, setMode] = useState<Mode>('delivery');
  const signedIn = session.status === 'signedIn' && session.user !== undefined;

  usePageMeta({ title: 'Pharmacies & health services near you', canonicalPath: '/nearby' });

  // A signed-in person who has not chosen a postcode here starts from their
  // default saved address. Asked once; a failure simply leaves the picker.
  const asked = useRef(false);
  useEffect(() => {
    if (place !== undefined || !signedIn || asked.current) return;
    asked.current = true;
    getSavedAddresses()
      .then((addresses) => {
        const chosen = addresses.find((a) => a.isDefault) ?? addresses[0];
        if (chosen !== undefined) return choose(chosen.postcode);
        return undefined;
      })
      .catch(() => undefined);
  }, [place, signedIn, choose]);

  const header = {
    location: (
      <div className="jt-pl-hdr">
        <LocationPicker place={place} recent={recent} choose={choose} />
        <div role="tablist" aria-label="How you want it" className="jt-pl-modes">
          {MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              role="tab"
              aria-selected={mode === m.id}
              className={`jt-pl-mode${mode === m.id ? ' jt-pl-mode--on' : ''}`}
              onClick={() => setMode(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
    ),
  };

  return (
    <SiteShell store={header}>
      {place === undefined ? (
        <NoPostcode choose={choose} />
      ) : (
        <PlacesBody key={place.postcode} place={place} mode={mode} setMode={setMode} />
      )}
    </SiteShell>
  );
}

// -- No postcode yet ----------------------------------------------------------------

function postcodeError(failure: unknown): string {
  if (failure instanceof SessionApiError && failure.status === 404) return 'We could not find that postcode.';
  if (failure instanceof SessionApiError && failure.status === 400)
    return 'That does not look like a UK postcode.';
  return 'We couldn’t check this postcode right now. Try again.';
}

function NoPostcode({ choose }: { readonly choose: (postcode: string) => Promise<Place> }): JSX.Element {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (text.trim().length === 0) {
      setError('Enter a postcode to see places near you.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await choose(text);
    } catch (failure) {
      setError(postcodeError(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="jt-pl-start" aria-labelledby="jt-pl-start-title">
      <span className="jt-pl-start__icon" aria-hidden="true">
        <Icon d={PATHS.pin} size={26} />
      </span>
      <h1 id="jt-pl-start-title">Pharmacies and health services near you</h1>
      <p>Enter your postcode to see the places that deliver to you, offer collection or see you in person.</p>
      <form onSubmit={(event) => void submit(event)} className="jt-pl-start__form">
        <label>
          <span className="jt-pl-sr">Postcode</span>
          <input
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="Enter your postcode"
            autoComplete="postal-code"
          />
        </label>
        <button type="submit" disabled={busy}>
          {busy ? 'Finding…' : 'Find places'}
        </button>
      </form>
      {error !== '' ? (
        <span role="alert" className="jt-pl-err">
          {error}
        </span>
      ) : null}
    </section>
  );
}

// -- The page ------------------------------------------------------------------------

function PlacesBody({
  place,
  mode,
  setMode,
}: {
  readonly place: Place;
  readonly mode: Mode;
  readonly setMode: (mode: Mode) => void;
}): JSX.Element {
  const now = useMemo(() => new Date(), []);
  const narrow = useWide('(max-width: 999px)');
  const [dept, setDept] = useState<DepartmentId | undefined>('pharmacy');
  const [needs, setNeeds] = useState<readonly string[]>([]);
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<Sort>('recommended');
  const [view, setView] = useState<'list' | 'map'>('list');
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [maxMiles, setMaxMiles] = useState<number | undefined>(undefined);
  const [regulator, setRegulator] = useState<string | undefined>(undefined);
  const [drawer, setDrawer] = useState(false);
  const [allOpen, setAllOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(text.trim()), 300);
    return () => clearTimeout(timer);
  }, [text]);

  const taxonomyLoad = useLoad('pl-taxonomy', getCategories);
  const taxonomy: readonly TaxonomyCategory[] = taxonomyLoad.status === 'loaded' ? taxonomyLoad.data : [];

  const anyFilter =
    Object.values(filters).some(Boolean) ||
    needs.length > 0 ||
    query.length >= 2 ||
    maxMiles !== undefined ||
    regulator !== undefined;
  const isBrowse = dept === undefined && !anyFilter && view === 'list';
  const deptCats = dept === undefined ? undefined : departmentCategories(taxonomy, dept);
  // A department the taxonomy holds nothing for has nothing near anyone.
  const deptEmpty = deptCats !== undefined && taxonomyLoad.status === 'loaded' && deptCats.length === 0;
  const waitingTaxonomy = deptCats !== undefined && taxonomyLoad.status === 'loading';

  const request: DirectoryRequest = {
    postcode: place.postcode,
    mode,
    need: needs,
    ...(deptCats !== undefined && deptCats.length > 0 ? { category: deptCats } : {}),
    ...(dept === 'services' ? { kind: 'SERVICE' as const } : {}),
    q: query,
    open: filters.open,
    ...(filters.four ? { rating: 4.5 } : {}),
    offers: filters.offers,
    freeDelivery: filters.freeDelivery,
    nhs: filters.nhs,
    ...(regulator !== undefined ? { regulator } : {}),
    ...(maxMiles !== undefined ? { maxMiles } : {}),
    sort: isBrowse ? 'nearest' : sort,
    limit: isBrowse ? 50 : PAGE,
  };
  const key = deptEmpty || waitingTaxonomy ? undefined : directoryQuery(request);
  const loaded = useLoad(key, () => getDirectory(request));
  const data = loaded.status === 'loaded' && key !== undefined ? loaded.data : undefined;

  // "Show more places" pages on from the same question.
  const [more, setMore] = useState<{
    key: string;
    places: readonly DirectoryPlace[];
    page: number;
    hasMore: boolean;
  }>({ key: '', places: [], page: 1, hasMore: false });
  const extra = more.key === key ? more : undefined;
  const places = data === undefined ? [] : [...data.places, ...(extra?.places ?? [])];
  const hasMore = extra !== undefined ? extra.hasMore : (data?.hasMore ?? false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMore = async (): Promise<void> => {
    if (key === undefined) return;
    setLoadingMore(true);
    const page = (extra?.page ?? 1) + 1;
    try {
      const next = await getDirectory({ ...request, page });
      setMore({ key, places: [...(extra?.places ?? []), ...next.places], page, hasMore: next.hasMore });
    } catch {
      // The button stays; the list is as it was.
    } finally {
      setLoadingMore(false);
    }
  };

  const area = place.displayArea !== undefined ? `, ${place.displayArea}` : '';
  const deptInfo = DEPARTMENTS.find((d) => d.id === dept);
  const needNames = needs
    .map((id) => taxonomy.find((c) => c.id === id)?.name)
    .filter((name): name is string => name !== undefined);
  const heading = isBrowse
    ? `Everything near ${place.postcode}${area}`
    : `${deptInfo?.plural ?? 'Places'}${needNames.length > 0 ? ` for ${needNames.join(', ').toLowerCase()}` : ''} near ${place.postcode}${area}`;
  const total = deptEmpty ? 0 : (data?.total ?? 0);
  const countLabel = `${String(total)} ${total === 1 ? 'place' : 'places'}${data !== undefined && !data.complete ? ' or more' : ''}`;

  const clearAll = (): void => {
    setFilters(NO_FILTERS);
    setNeeds([]);
    setText('');
    setQuery('');
    setMaxMiles(undefined);
    setRegulator(undefined);
    setDept(undefined);
    setView('list');
  };
  const toggleNeed = (ids: readonly string[]): void => {
    setNeeds((current) => {
      const on = ids.length > 0 && ids.every((id) => current.includes(id));
      return on ? current.filter((id) => !ids.includes(id)) : [...new Set([...current, ...ids])];
    });
  };
  const needCount = (ids: readonly string[]): number =>
    Math.max(0, ...ids.map((id) => data?.needs.find((n) => n.categoryId === id)?.places ?? 0));

  const filterPanel = (
    <FilterPanel
      heading={heading}
      countLabel={countLabel}
      filters={filters}
      setFilters={setFilters}
      nhsRecorded={data?.nhsRecorded ?? false}
      maxMiles={maxMiles}
      setMaxMiles={setMaxMiles}
      regulators={data?.regulators ?? []}
      regulator={regulator}
      setRegulator={setRegulator}
      clearAll={clearAll}
    />
  );
  const activeCount =
    Object.values(filters).filter(Boolean).length +
    (maxMiles !== undefined ? 1 : 0) +
    (regulator !== undefined ? 1 : 0);

  return (
    <div className="jt-pl">
      <nav aria-label="Categories" className="jt-pl-cats">
        {DEPARTMENTS.map((d) => {
          const on = dept === d.id;
          const image =
            d.match === undefined
              ? undefined
              : taxonomy.find((c) => d.match?.(c) === true && c.imageUrl)?.imageUrl;
          return (
            <button
              key={d.id}
              type="button"
              aria-pressed={on}
              className={`jt-pl-tile${on ? ' jt-pl-tile--on' : ''}`}
              onClick={() => setDept(on ? undefined : d.id)}
            >
              <span className="jt-pl-tile__img" style={{ background: d.bg }}>
                {image !== undefined ? <img src={image} alt="" loading="lazy" /> : null}
                {on ? (
                  <span className="jt-pl-tile__tick" aria-hidden="true">
                    <Icon d={PATHS.check} size={12} stroke="#fff" width={3} />
                  </span>
                ) : null}
              </span>
              <span className="jt-pl-tile__label">{d.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="jt-pl-grid">
        {!narrow ? (
          <aside aria-label="Filters" className="jt-pl-side">
            {filterPanel}
          </aside>
        ) : null}

        <section className="jt-pl-main" aria-label="Places">
          {narrow ? <h1 className="jt-pl-h1 jt-pl-h1--narrow">{heading}</h1> : null}
          <div className="jt-pl-bar">
            {narrow ? (
              <button
                type="button"
                className="jt-pl-pill"
                aria-expanded={drawer}
                onClick={() => setDrawer(true)}
              >
                <Icon d={PATHS.sort} size={16} stroke="#17112b" />
                Filters{activeCount > 0 ? ` (${String(activeCount)})` : ''}
              </button>
            ) : null}
            <label className="jt-pl-search">
              <Icon d={PATHS.search} size={17} />
              <input
                value={text}
                onChange={(event) => setText(event.target.value)}
                placeholder="Search treatments, products or pharmacies"
                aria-label="Search treatments, products or pharmacies"
              />
            </label>
            <div className="jt-pl-sort">
              <button
                type="button"
                className="jt-pl-pill"
                aria-haspopup="listbox"
                aria-expanded={sortOpen}
                aria-label={`Sort by: ${SORTS.find((s) => s.id === sort)?.label ?? ''}`}
                onClick={() => setSortOpen((open) => !open)}
              >
                <Icon d={PATHS.sort} size={16} />
                <span className="jt-pl-muted">Sort</span>
                <b>{SORTS.find((s) => s.id === sort)?.label}</b>
                <Icon d={PATHS.chevronDown} size={14} stroke="#17112b" />
              </button>
              {sortOpen ? (
                <div role="listbox" aria-label="Sort by" className="jt-pl-pop">
                  {SORTS.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      role="option"
                      aria-selected={s.id === sort}
                      onClick={() => {
                        setSort(s.id);
                        setSortOpen(false);
                      }}
                    >
                      {s.label}
                      {s.id === sort ? <Icon d={PATHS.check} size={16} /> : null}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className="jt-pl-pill"
              aria-label={view === 'map' ? 'Show list view' : 'Show map view'}
              onClick={() => setView(view === 'map' ? 'list' : 'map')}
            >
              <Icon d={view === 'map' ? PATHS.list : PATHS.pin} size={16} />
              {view === 'map' ? 'List' : 'Map'}
            </button>
          </div>

          <div className="jt-pl-needs" role="group" aria-label="Health needs">
            {NEEDS.map((n) => {
              const cats = needCategories(taxonomy, n.pattern);
              const ids = cats.map((c) => c.id);
              const on = ids.length > 0 && ids.every((id) => needs.includes(id));
              const available = ids.length > 0 && (data === undefined || needCount(ids) > 0 || on);
              const image = cats.find((c) => c.imageUrl !== undefined)?.imageUrl;
              return (
                <button
                  key={n.label}
                  type="button"
                  aria-pressed={on}
                  aria-disabled={ids.length === 0 ? true : undefined}
                  className={`jt-pl-need${on ? ' jt-pl-need--on' : ''}${available ? '' : ' jt-pl-need--dim'}`}
                  onClick={() => {
                    if (ids.length > 0) toggleNeed(ids);
                  }}
                >
                  <span className="jt-pl-need__img" style={{ background: n.bg }}>
                    {image !== undefined ? <img src={image} alt="" loading="lazy" /> : null}
                    {on ? (
                      <span className="jt-pl-tile__tick" aria-hidden="true">
                        <Icon d={PATHS.check} size={11} stroke="#fff" width={3} />
                      </span>
                    ) : null}
                  </span>
                  <span>{n.label}</span>
                </button>
              );
            })}
            <button type="button" className="jt-pl-need jt-pl-need--all" onClick={() => setAllOpen(true)}>
              <span className="jt-pl-need__img jt-pl-need__img--all">
                <Icon d={PATHS.chevronRight} size={18} />
              </span>
              <span>Show all</span>
            </button>
          </div>

          <Results
            place={place}
            mode={mode}
            setMode={setMode}
            now={now}
            view={view}
            setView={setView}
            isBrowse={isBrowse}
            deptEmpty={deptEmpty}
            deptLabel={deptInfo?.plural}
            loading={key === undefined ? waitingTaxonomy : loaded.status === 'loading'}
            error={loaded.status === 'error' && key !== undefined ? loaded : undefined}
            retry={loaded.reload}
            data={data}
            places={places}
            hasMore={hasMore}
            loadingMore={loadingMore}
            loadMore={() => void loadMore()}
            anyFilter={anyFilter}
            maxMiles={maxMiles}
            widen={() => setMaxMiles(10)}
            clearAll={clearAll}
          />

          <p className="jt-pl-fees">Fees are set by each place and shown in your basket before you pay.</p>
        </section>
      </div>

      {narrow && drawer ? (
        <div className="jt-pl-drawer" role="dialog" aria-modal="true" aria-label="Filters">
          <button
            type="button"
            className="jt-pl-scrim"
            aria-label="Close filters"
            onClick={() => setDrawer(false)}
          />
          <div className="jt-pl-drawer__panel">
            <div className="jt-pl-drawer__head">
              <span>FILTERS</span>
              <button type="button" className="jt-pl-cta" onClick={() => setDrawer(false)}>
                Show {countLabel}
              </button>
            </div>
            {filterPanel}
          </div>
        </div>
      ) : null}

      {allOpen ? (
        <AllNeeds
          taxonomy={taxonomy}
          counts={data?.needs ?? []}
          selected={needs}
          onPick={(id) => {
            toggleNeed([id]);
            setAllOpen(false);
          }}
          onClose={() => setAllOpen(false)}
        />
      ) : null}
    </div>
  );
}

// -- Filters --------------------------------------------------------------------------

function FilterPanel({
  heading,
  countLabel,
  filters,
  setFilters,
  nhsRecorded,
  maxMiles,
  setMaxMiles,
  regulators,
  regulator,
  setRegulator,
  clearAll,
}: {
  readonly heading: string;
  readonly countLabel: string;
  readonly filters: Filters;
  readonly setFilters: (next: Filters) => void;
  readonly nhsRecorded: boolean;
  readonly maxMiles: number | undefined;
  readonly setMaxMiles: (miles: number | undefined) => void;
  readonly regulators: readonly { readonly regulator: string; readonly places: number }[];
  readonly regulator: string | undefined;
  readonly setRegulator: (regulator: string | undefined) => void;
  readonly clearAll: () => void;
}): JSX.Element {
  const toggles: readonly { readonly k: keyof Filters; readonly label: string }[] = [
    { k: 'open', label: 'Open now' },
    { k: 'four', label: '4.5+ stars' },
    { k: 'offers', label: 'Offers' },
    { k: 'freeDelivery', label: 'Free delivery' },
    // Only when some place records NHS services; otherwise the filter would always empty the list.
    ...(nhsRecorded || filters.nhs ? [{ k: 'nhs' as const, label: 'NHS services' }] : []),
  ];
  return (
    <div className="jt-pl-filters">
      <div className="jt-pl-filters__top">
        <h1 className="jt-pl-h1">{heading}</h1>
        <div className="jt-pl-filters__count">
          <span>
            <span className="jt-pl-dot" aria-hidden="true" />
            <span>
              <b>{countLabel}</b>
              <small>Nearby</small>
            </span>
          </span>
          <button type="button" className="jt-pl-link" onClick={clearAll}>
            Clear filters
          </button>
        </div>
      </div>
      <div className="jt-pl-toggles">
        {toggles.map((t) => (
          <button
            key={t.k}
            type="button"
            role="switch"
            aria-checked={filters[t.k]}
            className="jt-pl-switch"
            onClick={() => setFilters({ ...filters, [t.k]: !filters[t.k] })}
          >
            <span>{t.label}</span>
            <span className={`jt-pl-switch__track${filters[t.k] ? ' jt-pl-switch__track--on' : ''}`}>
              <span />
            </span>
          </button>
        ))}
      </div>
      <div className="jt-pl-group">
        <span className="jt-pl-eyebrow">DISTANCE</span>
        <div className="jt-pl-dists">
          {DISTANCES.map((d) => (
            <button
              key={d}
              type="button"
              aria-pressed={maxMiles === d}
              className={`jt-pl-dist${maxMiles === d ? ' jt-pl-dist--on' : ''}`}
              onClick={() => setMaxMiles(maxMiles === d ? undefined : d)}
            >
              {d} mi
            </button>
          ))}
        </div>
      </div>
      {regulators.length > 0 ? (
        <div className="jt-pl-group">
          <span className="jt-pl-eyebrow">REGULATED BY</span>
          {regulators.map((r) => {
            const on = regulator?.toLowerCase() === r.regulator.toLowerCase();
            return (
              <button
                key={r.regulator}
                type="button"
                role="checkbox"
                aria-checked={on}
                className="jt-pl-check"
                onClick={() => setRegulator(on ? undefined : r.regulator)}
              >
                <span className={`jt-pl-check__box${on ? ' jt-pl-check__box--on' : ''}`} aria-hidden="true">
                  {on ? <Icon d={PATHS.check} size={12} stroke="#fff" width={3} /> : null}
                </span>
                {r.regulator} registered <span className="jt-pl-muted">({r.places})</span>
              </button>
            );
          })}
        </div>
      ) : null}
      <p className="jt-pl-note">
        Each place&apos;s registration, fees and hours are its own, as it published them. Fees are shown
        before you pay.
      </p>
    </div>
  );
}

// -- Results -----------------------------------------------------------------------------

function Results(props: {
  readonly place: Place;
  readonly mode: Mode;
  readonly setMode: (mode: Mode) => void;
  readonly now: Date;
  readonly view: 'list' | 'map';
  readonly setView: (view: 'list' | 'map') => void;
  readonly isBrowse: boolean;
  readonly deptEmpty: boolean;
  readonly deptLabel: string | undefined;
  readonly loading: boolean;
  readonly error: { readonly message: string } | undefined;
  readonly retry: () => void;
  readonly data: DirectoryPage | undefined;
  readonly places: readonly DirectoryPlace[];
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly loadMore: () => void;
  readonly anyFilter: boolean;
  readonly maxMiles: number | undefined;
  readonly widen: () => void;
  readonly clearAll: () => void;
}): JSX.Element {
  const { place, data, places } = props;
  if (props.deptEmpty) {
    return (
      <Empty
        title={`No ${(props.deptLabel ?? 'places').toLowerCase()} near ${place.postcode} yet`}
        body="Nothing in this category is listed on Just Treated yet. See everything near you instead."
        action="See everything near you"
        onAction={props.clearAll}
      />
    );
  }
  if (props.error !== undefined) {
    // Never "nobody near you": a failure to look is said as one.
    return (
      <div className="jt-pl-empty" role="alert">
        <b>We couldn’t check places near {place.postcode} right now.</b>
        <span>This is on our side. Try again in a moment.</span>
        <button type="button" className="jt-pl-cta" onClick={props.retry}>
          Try again
        </button>
      </div>
    );
  }
  if (props.loading || data === undefined) {
    return (
      <p className="jt-pl-muted" role="status">
        Finding places near {place.postcode}…
      </p>
    );
  }
  if (data.reachable === 0) return <Unserved postcode={place.postcode} />;
  if (data.total === 0) {
    const widen = props.maxMiles !== undefined && props.maxMiles < 10;
    return (
      <Empty
        title={`Nothing matches near ${place.postcode}`}
        body={
          widen
            ? `No match within ${String(props.maxMiles)} ${props.maxMiles === 1 ? 'mile' : 'miles'}. Searching up to 10 miles may find more.`
            : 'Try another way to get it, or remove a filter.'
        }
        action={widen ? 'Search within 10 miles' : 'Clear filters'}
        onAction={widen ? props.widen : props.clearAll}
      />
    );
  }
  if (props.view === 'map') {
    return (
      <MapView
        place={place}
        places={places}
        mode={props.mode}
        now={props.now}
        onList={() => props.setView('list')}
      />
    );
  }
  if (props.isBrowse) return <Browse places={places} total={data.total} now={props.now} />;
  return (
    <div className="jt-pl-list">
      {places.map((p) => (
        <PlaceCard key={p.id} place={p} mode={props.mode} now={props.now} />
      ))}
      {props.hasMore ? (
        <button type="button" className="jt-pl-more" disabled={props.loadingMore} onClick={props.loadMore}>
          {props.loadingMore ? 'Loading…' : 'Show more places'}
        </button>
      ) : null}
    </div>
  );
}

function Empty({
  title,
  body,
  action,
  onAction,
}: {
  readonly title: string;
  readonly body: string;
  readonly action: string;
  readonly onAction: () => void;
}): JSX.Element {
  return (
    <div className="jt-pl-empty">
      <span className="jt-pl-empty__icon" aria-hidden="true">
        <Icon d={PATHS.search} size={24} />
      </span>
      <b>{title}</b>
      <span>{body}</span>
      <button type="button" className="jt-pl-cta" onClick={onAction}>
        {action}
      </button>
    </div>
  );
}

/** Nobody serves this postcode yet: say so, and record the wish if they want. */
function Unserved({ postcode }: { readonly postcode: string }): JSX.Element {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'busy' | 'created' | 'known' | 'error'>('idle');
  const [error, setError] = useState('');
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setState('busy');
    try {
      const outcome = await registerInterest(postcode, email);
      setState(outcome.created ? 'created' : 'known');
    } catch (failure) {
      setState('error');
      setError(
        failure instanceof SessionApiError && failure.status === 429
          ? 'You’ve asked a few times already. Try again in a minute.'
          : failure instanceof SessionApiError && failure.status === 400
            ? 'Check the email address and try again.'
            : 'We couldn’t record that right now. Try again.',
      );
    }
  };
  return (
    <div className="jt-pl-empty jt-pl-unserved">
      <span className="jt-pl-empty__icon" aria-hidden="true">
        <Icon d={PATHS.pin} size={24} />
      </span>
      <b>We’re not in {postcode} yet</b>
      <span>
        No pharmacy or clinic on Just Treated reaches this postcode yet. We can let you know when one does.
      </span>
      {state === 'created' || state === 'known' ? (
        <span role="status" className="jt-pl-ok">
          {state === 'created'
            ? 'Thanks — we’ll let you know.'
            : 'You’re already on the list for this postcode.'}
        </span>
      ) : (
        <form onSubmit={(event) => void submit(event)} className="jt-pl-start__form">
          <label>
            <span className="jt-pl-sr">Email (optional)</span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="Email (optional)"
              autoComplete="email"
            />
          </label>
          <button type="submit" disabled={state === 'busy'}>
            {state === 'busy' ? 'Saving…' : 'Notify me'}
          </button>
        </form>
      )}
      {state === 'error' ? (
        <span role="alert" className="jt-pl-err">
          {error}
        </span>
      ) : null}
    </div>
  );
}

// -- Cards --------------------------------------------------------------------------------

function initials(name: string): string {
  return (
    name
      .split(/[^A-Za-z0-9]+/)
      .filter((w) => w.length > 0 && !/^(and|of|the)$/i.test(w))
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('') || '?'
  );
}

function PlaceCard({
  place,
  mode,
  now,
}: {
  readonly place: DirectoryPlace;
  readonly mode: Mode;
  readonly now: Date;
}): JSX.Element {
  const store = `/storefronts/${place.slug}`;
  const status = statusChip(place, now);
  const fulfil = fulfilChip(place, mode);
  const where = [distanceLabel(place.distanceMiles), whereLabel(place)].filter(Boolean).join(' · ');
  const rating = ratingLabel(place);
  return (
    <article className="jt-pl-card" aria-label={place.name}>
      <AppLink to={store} className="jt-pl-card__tile" ariaLabel={`View ${place.name}`}>
        <span className="jt-pl-bg" style={{ background: tileOf(place.id) }} aria-hidden="true" />
        <span className="jt-pl-card__blob" aria-hidden="true" />
        {rating !== undefined ? (
          <span className="jt-pl-card__rating">
            <b>{rating}</b>
            {place.reviewCount !== undefined ? (
              <small>
                {place.reviewCount.toLocaleString('en-GB')} {place.reviewCount === 1 ? 'review' : 'reviews'}
              </small>
            ) : null}
          </span>
        ) : null}
        <span className="jt-pl-card__initials" aria-hidden="true">
          {initials(place.name)}
        </span>
        <span className="jt-pl-card__type">{place.kindLabel}</span>
      </AppLink>
      <div className="jt-pl-card__body">
        <AppLink to={store} className="jt-pl-card__name">
          {place.name}
          {place.registration !== undefined ? (
            <span title={`${place.registration.regulator} ${place.registration.number}`}>
              <Icon d={PATHS.shield} size={17} />
            </span>
          ) : null}
        </AppLink>
        {where !== '' ? <span className="jt-pl-card__where">{where}</span> : null}
        <div className="jt-pl-chips">
          {status !== undefined ? (
            <span className={`jt-pl-chip ${status.open ? 'jt-pl-chip--open' : 'jt-pl-chip--closed'}`}>
              {status.label}
            </span>
          ) : null}
          {fulfil !== undefined ? <span className="jt-pl-chip jt-pl-chip--fulfil">{fulfil}</span> : null}
          {place.promotions.slice(0, 1).map((promotion) => (
            <span key={promotion} className="jt-pl-chip jt-pl-chip--offer">
              <Icon d={PATHS.tag} size={12} stroke="#5c3206" />
              {promotion}
            </span>
          ))}
          {place.nhs ? <span className="jt-pl-chip jt-pl-chip--fulfil">NHS services</span> : null}
        </div>
        {place.rows.map((offer) => {
          const row = rowOf(place.slug, offer);
          return (
            <div key={offer.id} className="jt-pl-row">
              <div>
                <span className="jt-pl-row__title">{row.title}</span>
                {row.sub !== undefined ? <span className="jt-pl-row__sub">{row.sub}</span> : null}
              </div>
              <span className="jt-pl-row__price">{row.price}</span>
              <AppLink
                to={row.to}
                className={`jt-pl-row__btn${row.action === 'Book' ? ' jt-pl-row__btn--book' : ''}`}
                ariaLabel={`${row.action} ${row.title}`}
              >
                {row.action}
              </AppLink>
            </div>
          );
        })}
        <AppLink to={store} className="jt-pl-card__all">
          {seeAllLabel(place)}
          <Icon d={PATHS.chevronRight} size={14} />
        </AppLink>
      </div>
    </article>
  );
}

function MiniCard({ place }: { readonly place: DirectoryPlace }): JSX.Element {
  const rating = ratingLabel(place);
  const dist = distanceLabel(place.distanceMiles);
  const headline = place.rows[0]?.title ?? place.kindLabel;
  return (
    <article className="jt-pl-mini">
      <AppLink
        to={`/storefronts/${place.slug}`}
        className="jt-pl-mini__tile"
        ariaLabel={`View ${place.name}`}
      >
        <span className="jt-pl-bg" style={{ background: tileOf(place.id) }} aria-hidden="true" />
        <span className="jt-pl-mini__head">{headline}</span>
        {place.promotions[0] !== undefined ? (
          <span className="jt-pl-mini__offer">{place.promotions[0]}</span>
        ) : null}
        <span className="jt-pl-mini__initials" aria-hidden="true">
          {initials(place.name)}
        </span>
      </AppLink>
      <div className="jt-pl-mini__text">
        <span className="jt-pl-mini__name">{place.name}</span>
        <span className="jt-pl-mini__meta">
          {[
            rating !== undefined
              ? `★ ${rating}${place.reviewCount !== undefined ? ` (${place.reviewCount.toLocaleString('en-GB')})` : ''}`
              : undefined,
            dist,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </div>
    </article>
  );
}

/** "Everything near …": rails only where the recorded values back them, then every place. */
function Browse({
  places,
  total,
}: {
  readonly places: readonly DirectoryPlace[];
  readonly total: number;
  readonly now: Date;
}): JSX.Element {
  const [info, setInfo] = useState(false);
  const rails: readonly { title: string; sub: string; items: readonly DirectoryPlace[] }[] = [
    {
      title: 'Top rated nearby',
      sub: 'Rated 4.8 or higher by customers',
      items: places
        .filter((p) => p.rating !== undefined && p.rating >= 4.8)
        .sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0) || (b.reviewCount ?? 0) - (a.reviewCount ?? 0)),
    },
    {
      title: 'Offers near you',
      sub: 'Running now at these places',
      items: places.filter((p) => p.promotions.length > 0),
    },
    { title: 'NHS services', sub: 'Places that list NHS services', items: places.filter((p) => p.nhs) },
    {
      title: 'Clinics & health services',
      sub: 'Book a consultation, test or treatment',
      items: places.filter((p) => p.services > 0),
    },
  ].filter((rail) => rail.items.length >= 2);
  return (
    <div className="jt-pl-browse">
      {rails.map((rail) => (
        <section key={rail.title} aria-label={rail.title} className="jt-pl-rail">
          <h2>{rail.title}</h2>
          <span className="jt-pl-muted">{rail.sub}</span>
          <div className="jt-pl-rail__track">
            {rail.items.map((p) => (
              <MiniCard key={p.id} place={p} />
            ))}
          </div>
        </section>
      ))}
      <section aria-label="All places" className="jt-pl-rail">
        <div className="jt-pl-rail__head">
          <h2>
            Order from {total} {total === 1 ? 'place' : 'places'}
          </h2>
          <button
            type="button"
            className="jt-pl-round"
            aria-expanded={info}
            aria-label="How places are ordered"
            onClick={() => setInfo(!info)}
          >
            <Icon d={PATHS.info} size={16} />
          </button>
        </div>
        {info ? (
          <p className="jt-pl-muted">
            Places are listed nearest first, measured from your postcode. No place pays to be listed higher.
          </p>
        ) : null}
        <div className="jt-pl-allgrid">
          {places.map((p) => (
            <MiniCard key={p.id} place={p} />
          ))}
        </div>
      </section>
    </div>
  );
}

// -- The map: our own drawing, to scale, no tiles (D7) -------------------------------------

const MAP_W = 1000;
const MAP_H = 620;

function MapView({
  place,
  places,
  mode,
  now,
  onList,
}: {
  readonly place: Place;
  readonly places: readonly DirectoryPlace[];
  readonly mode: Mode;
  readonly now: Date;
  readonly onList: () => void;
}): JSX.Element {
  const located = places.filter((p) => p.coordinates !== undefined);
  const [selected, setSelected] = useState(0);
  const index = Math.min(selected, Math.max(0, located.length - 1));
  const current = located[index];
  const centre = place.coordinates ?? located[0]?.coordinates;

  if (centre === undefined || located.length === 0) {
    return (
      <div className="jt-pl-empty">
        <b>None of these places has a map position.</b>
        <button type="button" className="jt-pl-cta" onClick={onList}>
          Return to list
        </button>
      </div>
    );
  }

  const { pins, pxPerMile } = projectPins(
    centre,
    located.map((p) => ({ id: p.id, ...(p.coordinates as { latitude: number; longitude: number }) })),
    MAP_W,
    MAP_H,
  );
  const rings = [1, 3, 5, 10].filter((m) => m * pxPerMile < MAP_H / 2 + 40 && m * pxPerMile > 30);
  const missing = places.length - located.length;
  const status = current !== undefined ? statusChip(current, now) : undefined;

  return (
    <div className="jt-pl-map">
      <svg
        viewBox={`0 0 ${String(MAP_W)} ${String(MAP_H)}`}
        className="jt-pl-map__svg"
        role="img"
        aria-label={`Map of places near ${place.postcode}`}
      >
        <rect width={MAP_W} height={MAP_H} fill="#eef0f3" />
        {Array.from({ length: 12 }, (_, i) => (
          <line
            key={`v${String(i)}`}
            x1={i * 90 + 20}
            y1={0}
            x2={i * 90 + 20}
            y2={MAP_H}
            stroke="#e2e6ea"
            strokeWidth={10}
          />
        ))}
        {Array.from({ length: 8 }, (_, i) => (
          <line
            key={`h${String(i)}`}
            x1={0}
            y1={i * 86 + 30}
            x2={MAP_W}
            y2={i * 86 + 30}
            stroke="#e2e6ea"
            strokeWidth={10}
          />
        ))}
        {rings.map((m) => (
          <g key={m}>
            <circle
              cx={MAP_W / 2}
              cy={MAP_H / 2}
              r={m * pxPerMile}
              fill="none"
              stroke="#c9bff0"
              strokeDasharray="6 6"
            />
            <text
              x={MAP_W / 2 + 6}
              y={MAP_H / 2 - m * pxPerMile - 6}
              fontSize="13"
              fill="#6b6385"
              fontWeight={700}
            >
              {m} mi
            </text>
          </g>
        ))}
        <circle cx={MAP_W / 2} cy={MAP_H / 2} r={11} fill="#5b2ede" stroke="#fff" strokeWidth={4} />
        <text
          x={MAP_W / 2}
          y={MAP_H / 2 + 34}
          textAnchor="middle"
          fontSize="14"
          fontWeight={800}
          fill="#17112b"
          stroke="#eef0f3"
          strokeWidth={4}
          paintOrder="stroke"
        >
          You · {place.postcode}
        </text>
        {pins.map((pin, i) => {
          const on = i === index;
          const r = on ? 24 : 19;
          return (
            <g
              key={pin.id}
              role="button"
              tabIndex={0}
              aria-label={`${String(i + 1)}. ${located[i]?.name ?? ''}`}
              className="jt-pl-pin"
              onClick={() => setSelected(i)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') setSelected(i);
              }}
            >
              <circle
                cx={pin.x}
                cy={pin.y}
                r={r}
                fill={on ? '#5b2ede' : '#fff'}
                stroke="#5b2ede"
                strokeWidth={3}
              />
              <text
                x={pin.x}
                y={pin.y + 6}
                textAnchor="middle"
                fontSize={on ? 18 : 16}
                fontWeight={800}
                fill={on ? '#fff' : '#5b2ede'}
              >
                {i + 1}
              </text>
            </g>
          );
        })}
      </svg>
      <button type="button" className="jt-pl-map__back" onClick={onList}>
        <Icon d={PATHS.list} size={16} stroke="#fff" />
        Return to list
      </button>
      {current !== undefined ? (
        <div className="jt-pl-map__card" aria-live="polite">
          <div className="jt-pl-map__nav">
            <button
              type="button"
              className="jt-pl-round"
              aria-label="Previous place"
              onClick={() => setSelected((index - 1 + located.length) % located.length)}
            >
              <Icon d={PATHS.chevronLeft} size={16} />
            </button>
            <span>
              {index + 1} / {located.length}
            </span>
            <button
              type="button"
              className="jt-pl-round"
              aria-label="Next place"
              onClick={() => setSelected((index + 1) % located.length)}
            >
              <Icon d={PATHS.chevronRight} size={16} />
            </button>
          </div>
          <div className="jt-pl-map__info">
            <span className="jt-pl-map__badge" style={{ background: tileOf(current.id) }} aria-hidden="true">
              {initials(current.name)}
            </span>
            <span className="jt-pl-map__text">
              <b>{current.name}</b>
              <span className="jt-pl-muted">{current.kindLabel}</span>
              <span className="jt-pl-map__facts">
                {[
                  current.rating !== undefined
                    ? `★ ${current.rating.toFixed(1)}${current.reviewCount !== undefined ? ` (${String(current.reviewCount)})` : ''}`
                    : undefined,
                  distanceLabel(current.distanceMiles),
                  status?.label,
                  fulfilChip(current, mode),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </span>
          </div>
          <div className="jt-pl-map__actions">
            <a
              className="jt-pl-pill"
              href={directionsUrl(current.coordinates as { latitude: number; longitude: number })}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon d={PATHS.directions} size={15} />
              Get directions
            </a>
            <AppLink to={`/storefronts/${current.slug}`} className="jt-pl-cta">
              View store
            </AppLink>
          </div>
        </div>
      ) : null}
      <p className="jt-pl-muted jt-pl-map__note">
        Drawn to scale from each place&apos;s premises; streets are not shown.
        {missing > 0
          ? ` ${String(missing)} ${missing === 1 ? 'place has' : 'places have'} no map position.`
          : ''}
      </p>
    </div>
  );
}

// -- All health needs ------------------------------------------------------------------------

function AllNeeds({
  taxonomy,
  counts,
  selected,
  onPick,
  onClose,
}: {
  readonly taxonomy: readonly TaxonomyCategory[];
  readonly counts: readonly { readonly categoryId: string; readonly places: number }[];
  readonly selected: readonly string[];
  readonly onPick: (id: string) => void;
  readonly onClose: () => void;
}): JSX.Element {
  const [q, setQ] = useState('');
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const term = q.trim().toLowerCase();
  const list = [...taxonomy]
    .filter((c) => c.name.toLowerCase().includes(term))
    .sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div className="jt-pl-modal-wrap">
      <button type="button" className="jt-pl-scrim" aria-label="Close" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label="All health needs" className="jt-pl-modal">
        <div className="jt-pl-modal__head">
          <span>All health needs</span>
          <button type="button" className="jt-pl-round" aria-label="Close" onClick={onClose}>
            <Icon d={PATHS.close} size={16} stroke="#17112b" />
          </button>
        </div>
        <label className="jt-pl-search">
          <Icon d={PATHS.search} size={17} />
          <input
            autoFocus
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="Search health needs"
            aria-label="Search health needs"
          />
        </label>
        <div className="jt-pl-modal__list">
          {list.map((c) => {
            const n = counts.find((x) => x.categoryId === c.id)?.places ?? 0;
            const on = selected.includes(c.id);
            return (
              <button
                key={c.id}
                type="button"
                aria-pressed={on}
                className={`jt-pl-modal__item${on ? ' jt-pl-modal__item--on' : ''}${n === 0 && !on ? ' jt-pl-modal__item--dim' : ''}`}
                onClick={() => onPick(c.id)}
              >
                <span>{c.name}</span>
                <span>({n})</span>
              </button>
            );
          })}
          {list.length === 0 ? (
            <span className="jt-pl-muted">No health needs match &quot;{q}&quot;</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
